import { ArrowChevronLeftIcon } from '@tableau/icons/ArrowChevronLeftIcon';
import { ArrowChevronRightIcon } from '@tableau/icons/ArrowChevronRightIcon';
import React from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { Messages } from '~src/codegen/Localize';

import { GuidedOnboardingBannerService, WelcomeBannerClickType } from '../services/GuidedOnboardingBannerService';
import { GuidedOnboardingStyles } from '../styles/GuidedOnboardingStyles';
import * as GuidedOnboardingFilmStripHooks from './GuidedOnboardingFilmStripHooks';
import { GuidedOnboardingFilmStripNavigationButton } from './GuidedOnboardingFilmStripNavigationButton';
import { GuidedOnboardingTestIds } from './GuidedOnboardingWelcomeBanner';

export interface GuidedOnboardingFilmStripProps {
    children: React.ReactNode;
}

const MIN_CARDS_PER_VIEW = 1;

export function GuidedOnboardingFilmStrip({ children }: GuidedOnboardingFilmStripProps): JSX.Element {
    const { width: totalContainerWidth, ref: containerRef } = useResizeDetector<HTMLDivElement>();
    const contentBodyRef = React.useRef<HTMLDivElement>(null);
    const prevButtonRef = React.useRef<HTMLButtonElement>(null);
    const nextButtonRef = React.useRef<HTMLButtonElement>(null);
    const { cardWidth, setFocus: focusFirstCard } = GuidedOnboardingFilmStripHooks.useFirstCard(contentBodyRef);
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const hasLayout = Number.isFinite(cardWidth) && Number.isFinite(totalContainerWidth);
    const cardsPerView = hasLayout ? Math.max(MIN_CARDS_PER_VIEW, Math.floor(totalContainerWidth / cardWidth)) : MIN_CARDS_PER_VIEW;
    const cardCount = React.Children.count(children);
    const maxIndex = Math.max(0, cardCount - cardsPerView);
    const effectiveCurrentIndex = Math.min(currentIndex, maxIndex);
    const indexForPrevPage = Math.max(0, effectiveCurrentIndex - cardsPerView);
    const indexForNextPage = Math.min(maxIndex, effectiveCurrentIndex + cardsPerView);
    const isPrevButtonDisabled = effectiveCurrentIndex <= 0;
    const isNextButtonDisabled = effectiveCurrentIndex >= (cardCount - cardsPerView);

    const handlePreviousClick = () => {
        setCurrentIndex(indexForPrevPage);
        setTimeout(() => {
            if (indexForPrevPage === 0) {
                nextButtonRef.current?.focus();
            }
        });
    };

    const handleNextClick = () => {
        setCurrentIndex(indexForNextPage);
        setTimeout(() => {
            if (indexForNextPage === maxIndex) {
                prevButtonRef.current?.focus();
            }
        });
    };

    const handleCardFocus = React.useCallback((event: FocusEvent) => {
        const contentBody = contentBodyRef.current;
        if (!contentBody) return;
        let cardElement = event.target as HTMLElement;
        while (cardElement && cardElement.parentElement !== contentBody) {
            cardElement = cardElement.parentElement as HTMLElement;
            if (!cardElement) return;
        }
        const focusedCardIndex = Array.from(contentBody.children).indexOf(cardElement);
        if (focusedCardIndex === -1) return;
        const targetViewIndex = Math.floor(focusedCardIndex / cardsPerView) * cardsPerView;
        if (targetViewIndex !== effectiveCurrentIndex) {
            setCurrentIndex(targetViewIndex);
        }
    }, [effectiveCurrentIndex, cardsPerView]);

    React.useEffect(() => {
        const contentBody = contentBodyRef.current;
        if (!contentBody) return;
        contentBody.addEventListener('focus', handleCardFocus, true);
        return () => {
            contentBody.removeEventListener('focus', handleCardFocus, true);
        };
    }, [handleCardFocus]);

    GuidedOnboardingFilmStripHooks.useScrollToCardEffect(effectiveCurrentIndex, contentBodyRef);

    React.useEffect(() => {
        GuidedOnboardingBannerService.instance.bannerLayoutChanged.trigger();
    }, [cardWidth, totalContainerWidth]);

    return (
        <div
            ref={containerRef}
            className={GuidedOnboardingStyles.filmStripContainer}
            data-tb-test-id={GuidedOnboardingTestIds.filmStrip}
            aria-label={Messages.guidedOnboardingFilmStrip()}
            role="region"
            tabIndex={-1}
            aria-live="polite"
        >

            <div className={GuidedOnboardingStyles.filmStripContentWrapper}>
                <div
                    ref={contentBodyRef}
                    className={GuidedOnboardingStyles.filmStripContentBody}
                    data-tb-test-id={GuidedOnboardingTestIds.filmStripContentBody}
                >
                    {children}
                </div>
            </div>

            {!isPrevButtonDisabled && (
                <GuidedOnboardingFilmStripNavigationButton
                    ref={prevButtonRef}
                    onClick={handlePreviousClick}
                    testId={GuidedOnboardingTestIds.filmStripPrevButton}
                    className={`${GuidedOnboardingStyles.filmStripPaginationButton} ${GuidedOnboardingStyles.filmStripPrevButton}`}
                    alt={Messages.guidedOnboardingFilmStrip_PreviousPage()}
                    Icon={ArrowChevronLeftIcon}
                    usageEventClickType={WelcomeBannerClickType[WelcomeBannerClickType.filmstripLeftArrow]}
                />
            )}

            {!isNextButtonDisabled && (
                <GuidedOnboardingFilmStripNavigationButton
                    ref={nextButtonRef}
                    onClick={handleNextClick}
                    testId={GuidedOnboardingTestIds.filmStripNextButton}
                    className={`${GuidedOnboardingStyles.filmStripPaginationButton} ${GuidedOnboardingStyles.filmStripNextButton}`}
                    alt={Messages.guidedOnboardingFilmStrip_NextPage()}
                    Icon={ArrowChevronRightIcon}
                    usageEventClickType={WelcomeBannerClickType[WelcomeBannerClickType.filmstripRightArrow]}
                />
            )}
        </div>
    );
}